import { useEffect, useState } from "react";
import { Loader2, FileDown, CalendarDays, Users, Timer, Filter } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const money = (v) => (v === null || v === undefined ? null : `$${Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`);
const rate = (r) => (r === null || r === undefined ? null : `${r}%`);
const hours = (h) => {
  if (h === null || h === undefined) return null;
  const d = Math.floor(h / 24);
  return d ? `${d}d ${Math.round(h % 24)}h` : `${h.toFixed(1)}h`;
};
const INSUFFICIENT = "Insufficient data for this metric.";

export function RFQReport() {
  const [data, setData] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [applied, setApplied] = useState({ start: "", end: "" });

  useEffect(() => {
    setData(null);
    api.get("/admin/rfq-analytics", { params: { start: applied.start || undefined, end: applied.end || undefined } })
      .then((r) => setData(r.data)).catch(() => setData({}));
  }, [applied]);

  const downloadPdf = async () => {
    try {
      const res = await api.get("/admin/rfq-analytics/report.pdf", { responseType: "blob", params: { start: applied.start || undefined, end: applied.end || undefined } });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "rfq-pipeline-report.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
  };

  const k = data?.kpis || {};
  const cards = [
    { label: "RFQ Win Rate", value: rate(k.win_rate), testid: "rfq-kpi-win-rate" },
    { label: "Avg RFQ → Quote", value: hours(k.avg_quote_hours), testid: "rfq-kpi-avg-quote-time" },
    { label: "Avg Order Value", value: money(k.avg_order_value), testid: "rfq-kpi-avg-order" },
    { label: "Total Quoted", value: money(k.total_quoted), testid: "rfq-kpi-quoted" },
    { label: "Total Won Value", value: money(k.total_won_value), testid: "rfq-kpi-won-value" },
    { label: "Paid Revenue", value: money(k.total_paid), testid: "rfq-kpi-paid" },
    { label: "Gross Profit", value: money(k.gross_profit), testid: "rfq-kpi-gp" },
    { label: "Gross Margin", value: rate(k.gross_margin), testid: "rfq-kpi-margin" },
  ];

  return (
    <div className="mt-5" data-testid="rfq-report-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-2.5 bg-white rounded-xl border border-slate-200 px-4 py-3" data-testid="rfq-report-daterange">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-[Poppins] font-semibold mb-1">From</label>
            <input data-testid="rfq-report-start" type="date" className="pg-input !py-2 !px-2.5 text-[13px]" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-[Poppins] font-semibold mb-1">To</label>
            <input data-testid="rfq-report-end" type="date" className="pg-input !py-2 !px-2.5 text-[13px]" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
          <button data-testid="rfq-report-apply" onClick={() => setApplied({ start, end })}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2.5 rounded-lg bg-[var(--pg-navy)] text-white hover:opacity-90">
            <Filter size={13} /> Apply
          </button>
          {(applied.start || applied.end) && (
            <button data-testid="rfq-report-clear" onClick={() => { setStart(""); setEnd(""); setApplied({ start: "", end: "" }); }}
              className="text-[12.5px] font-[Poppins] font-semibold px-2 py-2.5 text-slate-500 hover:text-[var(--pg-navy)]">Clear</button>
          )}
        </div>
        <button data-testid="rfq-report-download-pdf" onClick={downloadPdf}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2 rounded-lg bg-white border border-slate-200 text-slate-600 hover:border-[var(--pg-accent)] hover:text-[var(--pg-navy)]">
          <FileDown size={14} /> Export PDF
        </button>
      </div>

      {data === null ? (
        <div className="mt-8 text-slate-400 text-center py-8" data-testid="rfq-report-loading"><Loader2 className="animate-spin inline" size={20} /></div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {cards.map((c) => (
              <div key={c.label} data-testid={c.testid} className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="text-[11.5px] uppercase tracking-wider text-slate-500 font-[Poppins] font-semibold">{c.label}</div>
                {c.value === null
                  ? <div className="mt-2 text-[12px] text-slate-400 italic leading-snug">{INSUFFICIENT}</div>
                  : <div className="mt-1.5 font-[Poppins] font-extrabold text-[24px] text-[var(--pg-navy)] leading-none">{c.value}</div>}
              </div>
            ))}
          </div>

          <div className="mt-5 grid lg:grid-cols-2 gap-5 items-start">
            <Funnel funnel={data.funnel || []} />
            <Turnaround t={data.turnaround} />
          </div>

          <ByCustomer rows={data.by_customer || []} />
          <SupplierPerformance rows={data.supplier_performance || []} />
          <MonthlyTrend months={data.by_month || []} />
        </>
      )}
    </div>
  );
}

function Funnel({ funnel }) {
  const max = Math.max(1, ...funnel.map((f) => f.count));
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="rfq-report-funnel">
      <div className="font-[Poppins] font-bold text-[14px] text-[var(--pg-navy)]">Conversion Funnel</div>
      <div className="mt-4 space-y-3">
        {funnel.map((f) => (
          <div key={f.stage} data-testid={`funnel-stage-${f.stage.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}>
            <div className="flex items-center justify-between text-[12.5px]">
              <span className="font-medium text-[var(--pg-navy)]">{f.stage}</span>
              <span className="text-slate-500">
                <span className="font-[Poppins] font-bold text-[var(--pg-navy)]">{f.count}</span>
                {f.pct_of_prev !== null && f.pct_of_prev !== undefined && (
                  <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-[Poppins] font-semibold">{f.pct_of_prev}% of prev</span>
                )}
              </span>
            </div>
            <div className="mt-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
              <div className={`h-full rounded-full ${f.stage === "Lost" ? "bg-red-400" : f.stage === "Won / Orders" ? "bg-green-500" : "bg-[var(--pg-accent)]"}`}
                style={{ width: `${(f.count / max) * 100}%`, minWidth: f.count ? 6 : 0 }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Turnaround({ t }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="rfq-report-turnaround">
      <div className="font-[Poppins] font-bold text-[14px] text-[var(--pg-navy)] flex items-center gap-2"><Timer size={15} /> Quote Turnaround</div>
      <p className="mt-1 text-[12px] text-slate-400">Time from RFQ submission to the customer quote going out.</p>
      {!t ? (
        <p className="mt-4 text-[13px] text-slate-400 italic" data-testid="turnaround-empty">{INSUFFICIENT}</p>
      ) : (
        <div className="mt-4 grid grid-cols-3 gap-3">
          {[["Average", hours(t.avg_hours), `${t.samples} quote${t.samples === 1 ? "" : "s"}`, "turnaround-avg"],
            ["Fastest", hours(t.fastest.hours), t.fastest.rfq_number, "turnaround-fastest"],
            ["Longest", hours(t.longest.hours), t.longest.rfq_number, "turnaround-longest"]].map(([label, val, sub, tid]) => (
            <div key={label} className="rounded-lg bg-slate-50 border border-slate-100 p-3.5" data-testid={tid}>
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-[Poppins] font-semibold">{label}</div>
              <div className="mt-1 font-[Poppins] font-extrabold text-[19px] text-[var(--pg-navy)]">{val}</div>
              <div className="mt-0.5 text-[11px] text-slate-400">{sub}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ByCustomer({ rows }) {
  return (
    <div className="mt-5 bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="rfq-report-by-customer">
      <div className="px-5 pt-4 pb-1 font-[Poppins] font-bold text-[14px] text-[var(--pg-navy)] flex items-center gap-2"><Users size={15} /> Revenue by Customer</div>
      <table className="w-full text-left text-[13px] min-w-[860px]">
        <thead>
          <tr className="border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500 font-[Poppins]">
            <th className="px-5 py-2.5">Customer</th><th className="px-3 py-2.5 text-right">RFQs</th><th className="px-3 py-2.5 text-right">Orders</th>
            <th className="px-3 py-2.5 text-right">Quoted</th><th className="px-3 py-2.5 text-right">Invoiced</th><th className="px-3 py-2.5 text-right">Paid</th>
            <th className="px-3 py-2.5 text-right">Gross Profit</th><th className="px-5 py-2.5 text-right">Margin</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={8} className="px-5 py-6 text-center text-slate-400" data-testid="by-customer-empty">No RFQs in the selected range.</td></tr>}
          {rows.map((c) => (
            <tr key={c.email} className="border-b border-slate-100" data-testid={`customer-row-${c.email}`}>
              <td className="px-5 py-2.5">
                <div className="font-medium text-[var(--pg-navy)]">{c.company || c.name || c.email}</div>
                <div className="text-[11.5px] text-slate-400">{c.email}</div>
              </td>
              <td className="px-3 py-2.5 text-right text-slate-500">{c.rfqs}</td>
              <td className="px-3 py-2.5 text-right text-slate-500">{c.orders}</td>
              <td className="px-3 py-2.5 text-right text-slate-500">{money(c.quoted)}</td>
              <td className="px-3 py-2.5 text-right text-slate-500">{money(c.invoiced)}</td>
              <td className="px-3 py-2.5 text-right font-[Poppins] font-semibold text-green-700">{money(c.paid)}</td>
              <td className="px-3 py-2.5 text-right text-slate-500">{c.gross_profit === null ? "—" : money(c.gross_profit)}</td>
              <td className="px-5 py-2.5 text-right font-[Poppins] font-semibold">{c.gross_margin === null ? "—" : rate(c.gross_margin)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="px-5 py-2.5 text-[11px] text-slate-400 border-t border-slate-100">Invoiced/paid figures reflect only invoices recorded in the system. Gross profit is shown for accepted quotes only.</p>
    </div>
  );
}

function SupplierPerformance({ rows }) {
  return (
    <div className="mt-5 bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="rfq-report-supplier-performance">
      <div className="px-5 pt-4 pb-1 font-[Poppins] font-bold text-[14px] text-[var(--pg-navy)] flex items-center gap-2"><Timer size={15} /> Supplier Response Performance</div>
      <table className="w-full text-left text-[13px] min-w-[720px]">
        <thead>
          <tr className="border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500 font-[Poppins]">
            <th className="px-5 py-2.5">Supplier</th><th className="px-3 py-2.5 text-right">Requests</th><th className="px-3 py-2.5 text-right">Responded</th>
            <th className="px-3 py-2.5 text-right">Declined</th><th className="px-3 py-2.5 text-right">Response Rate</th><th className="px-5 py-2.5 text-right">Avg Response Time</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={6} className="px-5 py-6 text-center text-slate-400 italic" data-testid="supplier-perf-empty">{INSUFFICIENT}</td></tr>}
          {rows.map((r) => (
            <tr key={r.supplier} className="border-b border-slate-100" data-testid={`supplier-perf-${r.supplier}`}>
              <td className="px-5 py-2.5 font-medium text-[var(--pg-navy)]">{r.supplier}</td>
              <td className="px-3 py-2.5 text-right text-slate-500">{r.sent}</td>
              <td className="px-3 py-2.5 text-right text-slate-500">{r.responded}</td>
              <td className="px-3 py-2.5 text-right text-slate-500">{r.declined}</td>
              <td className="px-3 py-2.5 text-right font-[Poppins] font-semibold">{r.response_rate === null ? "—" : `${r.response_rate}%`}</td>
              <td className="px-5 py-2.5 text-right text-slate-500">{r.avg_response_hours === null ? "—" : hours(r.avg_response_hours)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MonthlyTrend({ months }) {
  const max = Math.max(1, ...months.map((m) => Math.max(m.received, m.quoted, m.won)));
  const hasData = months.some((m) => m.received || m.quoted || m.won);
  const label = (k) => new Date(k + "-15").toLocaleDateString("en-US", { month: "short", year: months.length > 12 ? "2-digit" : undefined });
  return (
    <div className="mt-5 bg-white rounded-xl border border-slate-200 p-5" data-testid="rfq-report-monthly-trend">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-[Poppins] font-bold text-[14px] text-[var(--pg-navy)] flex items-center gap-2"><CalendarDays size={15} /> Monthly RFQ Trend</div>
        <div className="flex items-center gap-4 text-[11.5px] text-slate-500">
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-slate-300"></span> RFQs received</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[var(--pg-accent)]"></span> Quotes sent</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-green-500"></span> Orders won</span>
        </div>
      </div>
      {!hasData && <p className="mt-4 text-[13px] text-slate-400 italic" data-testid="rfq-trend-empty">{INSUFFICIENT}</p>}
      <div className="mt-4 flex items-end gap-1.5 h-28">
        {months.map((m) => (
          <div key={m.month} className="flex-1 flex flex-col items-center gap-1 min-w-0" data-testid={`rfq-trend-month-${m.month}`}>
            <div className="w-full flex items-end justify-center gap-0.5 h-20">
              <div className="w-1/4 bg-slate-300 rounded-t" title={`${m.received} received`} style={{ height: `${(m.received / max) * 100}%`, minHeight: m.received ? 4 : 0 }} />
              <div className="w-1/4 bg-[var(--pg-accent)] rounded-t" title={`${m.quoted} quotes sent`} style={{ height: `${(m.quoted / max) * 100}%`, minHeight: m.quoted ? 4 : 0 }} />
              <div className="w-1/4 bg-green-500 rounded-t" title={`${m.won} orders won`} style={{ height: `${(m.won / max) * 100}%`, minHeight: m.won ? 4 : 0 }} />
            </div>
            <span className="text-[9.5px] text-slate-400 font-[Poppins]">{label(m.month)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
